import {BiomatchResponse} from '@chekin/api';
import {
  IMAGE_SERVICE_ORIGIN,
  IV_DOCUMENT_IMAGE_TYPES,
  OCRDetectedData,
} from '@chekin/core';
import {ANALYTIC_EVENTS} from '../../../analytics';
import {OCRFailureResolution, StoredImageDetails, TranslationFn} from './types';

function getProcessModalReasons(
  t: TranslationFn,
  docType?: IV_DOCUMENT_IMAGE_TYPES,
) {
  if (docType === IV_DOCUMENT_IMAGE_TYPES.selfie) {
    return [
      t('iv_failed_possible_reasons.covered_accessory'),
      t('iv_failed_possible_reasons.poor_or_side_lighting'),
    ];
  }

  return [
    t('iv_failed_possible_reasons.the_document_is_cropped'),
    t('iv_failed_possible_reasons.the_photo_is_of_poor_quality'),
    t('iv_failed_possible_reasons.there_are_reflections'),
  ];
}

function buildOCRImageDetails(
  image: string,
  docType: IV_DOCUMENT_IMAGE_TYPES,
  data?: OCRDetectedData,
): StoredImageDetails {
  return {
    image,
    type: docType,
    mrzDetected: data?.mrz_detected,
    barcodeDetected: data?.barcode_detected,
    id: data?.id,
    service: IMAGE_SERVICE_ORIGIN.ocr,
    uploaded: true,
  };
}

function buildBiomatchImageDetails(
  image: string,
  docType: IV_DOCUMENT_IMAGE_TYPES,
  data?: BiomatchResponse,
): StoredImageDetails {
  return {
    image,
    type: docType,
    faceDetected: data?.is_face_detected,
    id: data?.id,
    service: IMAGE_SERVICE_ORIGIN.biomatch,
    uploaded: true,
  };
}

function omitRawImage(details: StoredImageDetails) {
  const {image: _, ...detailsWithoutRawImage} = details;
  return detailsWithoutRawImage;
}

function shouldPersistOCRImage(args: {
  isBiomatchAndPassport: boolean;
  onlyScanning?: boolean;
  docType?: IV_DOCUMENT_IMAGE_TYPES;
}) {
  return Boolean(!args.isBiomatchAndPassport && !args.onlyScanning && args.docType);
}

function resolveOCRFailure(args: {
  documentTypeWithoutMrz?: boolean;
  documentTypeBothSidesMrz?: boolean;
  hasFrontSideImage: boolean;
  docType?: IV_DOCUMENT_IMAGE_TYPES;
}): OCRFailureResolution {
  if (args.documentTypeWithoutMrz) {
    return {type: 'success_without_mrz'};
  }

  if (
    args.documentTypeBothSidesMrz &&
    args.hasFrontSideImage &&
    args.docType === IV_DOCUMENT_IMAGE_TYPES.back
  ) {
    return {type: 'retry_front_side_as_ocr'};
  }

  return {type: 'fail'};
}

function shouldRunOCRAfterBiomatch(args: {
  isBiomatchAndPassport: boolean;
  documentTypeWithoutMrz?: boolean;
}) {
  return args.isBiomatchAndPassport && !args.documentTypeWithoutMrz;
}

function getDocumentCompletionEvent(docType: IV_DOCUMENT_IMAGE_TYPES) {
  if (docType === IV_DOCUMENT_IMAGE_TYPES.front) {
    return ANALYTIC_EVENTS.ivFinishingFrontSideDocument;
  }

  if (docType === IV_DOCUMENT_IMAGE_TYPES.back) {
    return ANALYTIC_EVENTS.ivFinishingBackSideDocument;
  }

  return null;
}

export {
  buildBiomatchImageDetails,
  buildOCRImageDetails,
  getDocumentCompletionEvent,
  getProcessModalReasons,
  omitRawImage,
  resolveOCRFailure,
  shouldPersistOCRImage,
  shouldRunOCRAfterBiomatch,
};
